import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { 
  Bot, Target, ListOrdered, Heart, CheckSquare, ListTodo, FileText, Calendar, 
  Save, Plus, Trash2, Edit2, Mail, Clock, ChevronDown, ChevronRight, BarChart3,
  TrendingUp, Lightbulb, Brain, Sparkles, Flag, Trophy, RefreshCw, Compass, 
  MessageSquare, Palette, Search, Zap, Star, Gift, Cake, CalendarHeart
} from "lucide-react";

type FrequencyType = 'hourly' | 'daily' | 'weekly' | 'monthly' | 'quarterly';
type DataSourceType = 'goals' | 'affirmations' | 'gratefuls' | 'habits' | 'tasks' | 'journal' | 'calendar' | 'importantDates';

interface ConstraintOption {
  key: string;
  label: string;
  icon: any;
}

interface DataSourceConfig {
  label: string;
  icon: any;
  constraints: ConstraintOption[];
}

const dataSourceConfigs: Record<DataSourceType, DataSourceConfig> = {
  goals: {
    label: "My Goals",
    icon: Target,
    constraints: [
      { key: "progressInsights", label: "Progress Insights", icon: TrendingUp },
      { key: "milestoneRecommendations", label: "Milestone Recommendations", icon: Flag },
      { key: "prioritizationTips", label: "Goal Prioritization", icon: ListOrdered },
      { key: "timelineAdjustments", label: "Timeline Adjustments", icon: Clock },
      { key: "successStrategies", label: "Success Strategies", icon: Trophy },
      { key: "obstacleIdentification", label: "Obstacle Identification", icon: Search },
      { key: "goalAlignment", label: "Goal Alignment Analysis", icon: Compass },
    ]
  },
  affirmations: {
    label: "Affirmations",
    icon: ListOrdered,
    constraints: [
      { key: "personalizedSuggestions", label: "Personalized Suggestions", icon: Sparkles },
      { key: "effectivenessAnalysis", label: "Effectiveness Analysis", icon: BarChart3 },
      { key: "themeRecommendations", label: "Theme Recommendations", icon: Palette },
      { key: "motivationBoosters", label: "Motivation Boosters", icon: Zap },
      { key: "mindsetShifts", label: "Mindset Shift Suggestions", icon: Brain },
      { key: "dailyFocus", label: "Daily Focus Affirmations", icon: Target },
    ]
  },
  gratefuls: {
    label: "Gratefuls",
    icon: Heart,
    constraints: [
      { key: "patternInsights", label: "Gratitude Pattern Insights", icon: TrendingUp },
      { key: "moodCorrelation", label: "Mood Correlation Analysis", icon: Heart },
      { key: "expansionSuggestions", label: "Gratitude Expansion", icon: Sparkles },
      { key: "positivityTrends", label: "Positivity Trend Analysis", icon: BarChart3 },
      { key: "appreciationReminders", label: "Appreciation Reminders", icon: RefreshCw },
      { key: "deeperReflection", label: "Deeper Reflection Prompts", icon: Brain },
    ]
  },
  habits: {
    label: "Habits",
    icon: CheckSquare,
    constraints: [
      { key: "streakAnalysis", label: "Streak Analysis", icon: TrendingUp },
      { key: "stackingSuggestions", label: "Habit Stacking", icon: ListOrdered },
      { key: "consistencyTips", label: "Consistency Improvement", icon: RefreshCw },
      { key: "goalAlignment", label: "Habit-Goal Alignment", icon: Target },
      { key: "optimizationRecommendations", label: "Habit Optimization", icon: Clock },
      { key: "triggerIdentification", label: "Trigger Identification", icon: Zap },
      { key: "progressCelebration", label: "Progress Celebration", icon: Trophy },
    ]
  },
  tasks: {
    label: "Tasks",
    icon: ListTodo,
    constraints: [
      { key: "prioritizationSuggestions", label: "Task Prioritization", icon: Flag },
      { key: "productivityPatterns", label: "Productivity Patterns", icon: BarChart3 },
      { key: "timeEstimates", label: "Time Management Insights", icon: Clock },
      { key: "completionPredictions", label: "Completion Predictions", icon: TrendingUp },
      { key: "workloadBalance", label: "Workload Balance", icon: Compass },
      { key: "focusRecommendations", label: "Focus Recommendations", icon: Target },
      { key: "delegationSuggestions", label: "Delegation Suggestions", icon: Lightbulb },
    ]
  },
  journal: {
    label: "Journal",
    icon: FileText,
    constraints: [
      { key: "emotionalPatterns", label: "Emotional Pattern Analysis", icon: Heart },
      { key: "themeExtraction", label: "Theme Extraction", icon: Search },
      { key: "reflectionPrompts", label: "Reflection Prompts", icon: MessageSquare },
      { key: "growthTracking", label: "Personal Growth Tracking", icon: TrendingUp },
      { key: "insightSummaries", label: "Insight Summaries", icon: Lightbulb },
      { key: "connectionFinding", label: "Connection Finding", icon: Compass },
      { key: "writingEnhancement", label: "Writing Enhancement Tips", icon: Sparkles },
    ]
  },
  calendar: {
    label: "Calendar",
    icon: Calendar,
    constraints: [
      { key: "scheduleOptimization", label: "Schedule Optimization", icon: Clock },
      { key: "timeAllocation", label: "Time Allocation Insights", icon: BarChart3 },
      { key: "meetingEfficiency", label: "Meeting Efficiency Tips", icon: Zap },
      { key: "workLifeBalance", label: "Work-Life Balance Analysis", icon: Compass },
      { key: "conflictPrevention", label: "Conflict Prevention", icon: Search },
      { key: "energyManagement", label: "Energy Management", icon: TrendingUp },
      { key: "bufferRecommendations", label: "Buffer Recommendations", icon: RefreshCw },
    ]
  },
  importantDates: {
    label: "Important Dates",
    icon: Star,
    constraints: [
      { key: "upcomingEvents", label: "Upcoming Events", icon: CalendarHeart },
      { key: "giftSuggestions", label: "Gift Suggestions", icon: Gift },
      { key: "anniversaryReminders", label: "Anniversary Reminders", icon: Heart },
      { key: "birthdayReminders", label: "Birthday Reminders", icon: Cake },
    ]
  }
};

const frequencyOptions: { value: FrequencyType; label: string; description: string }[] = [
  { value: 'hourly', label: 'Hourly', description: 'Receive updates every hour' },
  { value: 'daily', label: 'Daily', description: 'Receive a daily summary' },
  { value: 'weekly', label: 'Weekly', description: 'Receive a weekly digest' },
  { value: 'monthly', label: 'Monthly', description: 'Receive a monthly overview' },
  { value: 'quarterly', label: 'Quarterly', description: 'Receive a quarterly review' },
];

interface ReportDataSource {
  enabled: boolean;
  constraints: Record<string, boolean>;
}

interface ReportVersion {
  id: string;
  name: string;
  dataSources: Record<DataSourceType, ReportDataSource>;
  frequency: FrequencyType;
  emailEnabled: boolean;
  email: string;
}

const createDefaultDataSources = (): Record<DataSourceType, ReportDataSource> => {
  const dataSources: Record<DataSourceType, ReportDataSource> = {} as any;
  (Object.keys(dataSourceConfigs) as DataSourceType[]).forEach(key => {
    const constraints: Record<string, boolean> = {};
    dataSourceConfigs[key].constraints.forEach(c => {
      constraints[c.key] = true;
    });
    dataSources[key] = { enabled: false, constraints };
  });
  return dataSources;
};

const createNewReport = (name: string = "New Report"): ReportVersion => ({
  id: crypto.randomUUID(),
  name,
  dataSources: createDefaultDataSources(),
  frequency: 'weekly',
  emailEnabled: false,
  email: '',
});

export function ReportingContent() {
  const { toast } = useToast();
  const [reports, setReports] = useState<ReportVersion[]>(() => {
    const saved = localStorage.getItem('ai-coach-reports');
    if (saved) {
      return JSON.parse(saved);
    }
    return [createNewReport("Weekly Summary")];
  });
  
  const [editingReport, setEditingReport] = useState<ReportVersion | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [expandedSources, setExpandedSources] = useState<Record<string, boolean>>({});
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const saveReports = (updatedReports: ReportVersion[]) => {
    setReports(updatedReports);
    localStorage.setItem('ai-coach-reports', JSON.stringify(updatedReports));
  };

  const handleAddReport = () => {
    const newReport = createNewReport(`Report ${reports.length + 1}`);
    setEditingReport(newReport);
    setExpandedSources({});
    setIsDialogOpen(true);
  };

  const handleEditReport = (report: ReportVersion) => {
    setEditingReport({ ...report, dataSources: JSON.parse(JSON.stringify(report.dataSources)) });
    setExpandedSources({});
    setIsDialogOpen(true);
  };

  const handleDeleteReport = (id: string) => {
    const updatedReports = reports.filter(r => r.id !== id);
    saveReports(updatedReports);
    setDeleteConfirmId(null);
    toast({
      title: "Report deleted",
      description: "The report configuration has been removed.",
    });
  };

  const handleSaveReport = () => {
    if (!editingReport) return;
    
    if (!editingReport.name.trim()) {
      toast({
        title: "Name required",
        description: "Please enter a name for this report.",
        variant: "destructive",
      });
      return;
    }
    
    if (editingReport.emailEnabled) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!editingReport.email.trim() || !emailRegex.test(editingReport.email.trim())) {
        toast({
          title: "Valid email required",
          description: "Please enter a valid email address for notifications.",
          variant: "destructive",
        });
        return;
      }
    }
    
    const existingIndex = reports.findIndex(r => r.id === editingReport.id);
    let updatedReports: ReportVersion[];
    
    if (existingIndex >= 0) {
      updatedReports = [...reports];
      updatedReports[existingIndex] = editingReport;
    } else {
      updatedReports = [...reports, editingReport];
    }
    
    saveReports(updatedReports);
    setIsDialogOpen(false);
    setEditingReport(null);
    toast({
      title: "Report saved",
      description: `"${editingReport.name}" has been saved.`,
    });
  };

  const toggleDataSource = (source: DataSourceType) => {
    if (!editingReport) return;
    setEditingReport({
      ...editingReport,
      dataSources: {
        ...editingReport.dataSources,
        [source]: {
          ...editingReport.dataSources[source],
          enabled: !editingReport.dataSources[source].enabled
        }
      }
    });
  };

  const toggleConstraint = (source: DataSourceType, constraint: string) => {
    if (!editingReport) return;
    setEditingReport({
      ...editingReport,
      dataSources: {
        ...editingReport.dataSources,
        [source]: {
          ...editingReport.dataSources[source],
          constraints: {
            ...editingReport.dataSources[source].constraints,
            [constraint]: !editingReport.dataSources[source].constraints[constraint]
          }
        }
      }
    });
  };

  const toggleExpandSource = (source: string) => {
    setExpandedSources(prev => ({
      ...prev,
      [source]: !prev[source]
    }));
  };

  const getEnabledSourcesCount = (report: ReportVersion) => {
    return Object.values(report.dataSources).filter(ds => ds.enabled).length;
  };

  const getEnabledConstraintsCount = (dataSource: ReportDataSource) => {
    return Object.values(dataSource.constraints).filter(Boolean).length;
  };

  return (
    <div className="w-full max-w-full p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-ai-reporting-title">AI Coach Reporting</h1>
            <p className="text-muted-foreground">Configure automated AI coaching reports and notifications</p>
          </div>
        </div>
        <Button onClick={handleAddReport} data-testid="button-add-report">
          <Plus className="w-4 h-4 mr-2" />
          Add Report
        </Button>
      </div>

      {reports.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Bot className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Reports Configured</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create your first AI coaching report to receive personalized insights via email.
            </p>
            <Button onClick={handleAddReport}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Report
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <Card key={report.id} data-testid={`card-report-${report.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{report.name}</CardTitle>
                    <CardDescription className="flex items-center gap-4 mt-1">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {frequencyOptions.find(f => f.value === report.frequency)?.label}
                      </span>
                      <span className="flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        {getEnabledSourcesCount(report)} data sources
                      </span>
                      {report.emailEnabled && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {report.email || "No email set"}
                        </span>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleEditReport(report)}
                      data-testid={`button-edit-report-${report.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Dialog open={deleteConfirmId === report.id} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => setDeleteConfirmId(report.id)}
                          data-testid={`button-delete-report-${report.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Delete Report</DialogTitle>
                          <DialogDescription>
                            Are you sure you want to delete "{report.name}"? This action cannot be undone.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
                          <Button variant="destructive" onClick={() => handleDeleteReport(report.id)}>Delete</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {(Object.entries(report.dataSources) as [DataSourceType, ReportDataSource][])
                    .filter(([_, ds]) => ds.enabled)
                    .map(([key, ds]) => {
                      const config = dataSourceConfigs[key];
                      const Icon = config.icon;
                      const enabledCount = getEnabledConstraintsCount(ds);
                      return (
                        <div 
                          key={key}
                          className="flex items-center gap-1.5 px-2.5 py-1 bg-muted rounded-md text-sm"
                        >
                          <Icon className="w-3.5 h-3.5" />
                          <span>{config.label}</span>
                          <span className="text-muted-foreground">({enabledCount})</span>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingReport && reports.some(r => r.id === editingReport.id) ? 'Edit Report' : 'Create Report'}</DialogTitle>
            <DialogDescription>
              Configure which AI insights to include and how often to receive them.
            </DialogDescription>
          </DialogHeader>

          {editingReport && (
            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <Label htmlFor="report-name">Report Name</Label>
                <Input
                  id="report-name"
                  value={editingReport.name}
                  onChange={(e) => setEditingReport({ ...editingReport, name: e.target.value })}
                  placeholder="e.g., Daily Tasks Summary"
                  data-testid="input-report-name"
                />
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold">Data Sources</Label>
                <p className="text-sm text-muted-foreground">Select which areas to include in this report and configure specific insights.</p>
                
                <div className="space-y-2">
                  {(Object.entries(dataSourceConfigs) as [DataSourceType, DataSourceConfig][]).map(([key, config]) => {
                    const Icon = config.icon;
                    const isEnabled = editingReport.dataSources[key].enabled;
                    const isExpanded = expandedSources[key];
                    const enabledCount = getEnabledConstraintsCount(editingReport.dataSources[key]);
                    const totalCount = config.constraints.length;

                    return (
                      <div key={key} className="border rounded-lg">
                        <div className="flex items-center justify-between p-3">
                          <div className="flex items-center gap-3">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => isEnabled && toggleExpandSource(key)}
                              disabled={!isEnabled}
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </Button>
                            <div className="w-8 h-8 bg-muted rounded-md flex items-center justify-center">
                              <Icon className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <div>
                              <Label className="text-base font-medium cursor-pointer">
                                {config.label}
                              </Label>
                              {isEnabled && (
                                <p className="text-xs text-muted-foreground">
                                  {enabledCount} of {totalCount} insights enabled
                                </p>
                              )}
                            </div>
                          </div>
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={() => toggleDataSource(key)}
                            data-testid={`switch-source-${key}`}
                          />
                        </div>

                        {isEnabled && isExpanded && (
                          <div className="border-t px-3 py-2 space-y-2 bg-muted/30">
                            {config.constraints.map((constraint) => {
                              const ConstraintIcon = constraint.icon;
                              const isConstraintEnabled = editingReport.dataSources[key].constraints[constraint.key];
                              return (
                                <div key={constraint.key} className="flex items-center justify-between py-1 pl-10">
                                  <div className="flex items-center gap-2">
                                    <ConstraintIcon className="w-3.5 h-3.5 text-muted-foreground" />
                                    <span className="text-sm">{constraint.label}</span>
                                  </div>
                                  <Switch
                                    checked={isConstraintEnabled}
                                    onCheckedChange={() => toggleConstraint(key, constraint.key)}
                                    data-testid={`switch-constraint-${key}-${constraint.key}`}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t">
                <Label className="text-base font-semibold">Frequency of Notifications</Label>
                <p className="text-sm text-muted-foreground">How often would you like to receive this report?</p>
                
                <Select 
                  value={editingReport.frequency} 
                  onValueChange={(value: FrequencyType) => setEditingReport({ ...editingReport, frequency: value })}
                >
                  <SelectTrigger data-testid="select-frequency">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {frequencyOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex flex-col">
                          <span>{option.label}</span>
                          <span className="text-xs text-muted-foreground">{option.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">Receive this report via email</p>
                  </div>
                  <Switch
                    checked={editingReport.emailEnabled}
                    onCheckedChange={(checked) => setEditingReport({ ...editingReport, emailEnabled: checked })}
                    data-testid="switch-email-enabled"
                  />
                </div>

                {editingReport.emailEnabled && (
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        value={editingReport.email}
                        onChange={(e) => setEditingReport({ ...editingReport, email: e.target.value })}
                        placeholder="your@email.com"
                        className="flex-1"
                        data-testid="input-email"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveReport} data-testid="button-save-report">
              <Save className="w-4 h-4 mr-2" />
              Save Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AICoachReportingPage() {
  return <ReportingContent />;
}
